import {
  workerPaySurveyAnswerBody,
  type WorkerPaySurveyAnswerDto,
  workerPaySurveyAnswerDto,
} from "@clipboard-health/contract-worker-app-bff";
import { post } from "@src/appV2/api";
import { environmentConfig } from "@src/appV2/environment";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib/analytics";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { useMutation, type UseMutationOptions, useQueryClient } from "@tanstack/react-query";
import { type AxiosError } from "axios";

import { workerPaySurveyQueryKeys } from "./queryKeys";

const WORKER_PAY_SURVEY_ANSWERS_PATH = "/worker-pay-survey-answers";
const WORKER_PAY_SURVEY_ANSWERS_URL = `${environmentConfig.REACT_APP_WORKER_APP_BFF_URL}${WORKER_PAY_SURVEY_ANSWERS_PATH}`;

interface CreateWorkerPaySurveyAnswerRequest {
  workerPaySurveyId: string;
  estimatedPayInDollars: number;
  notificationThresholdInDollars?: number;
}

export function useCreateWorkerPaySurveyAnswer(
  options: UseMutationOptions<
    WorkerPaySurveyAnswerDto,
    AxiosError,
    CreateWorkerPaySurveyAnswerRequest
  > = {}
) {
  const queryClient = useQueryClient();
  const { userId } = useDefinedWorker();

  const { onSuccess: userOnSuccess, ...restOptions } = options;

  return useMutation({
    mutationFn: async ({
      workerPaySurveyId,
      estimatedPayInDollars,
      notificationThresholdInDollars,
    }: CreateWorkerPaySurveyAnswerRequest) => {
      const response = await post({
        url: WORKER_PAY_SURVEY_ANSWERS_URL,
        data: {
          data: {
            type: "worker-pay-survey-answer" as const,
            attributes: {
              workerPaySurveyId,
              estimatedPayInMinorUnits: Math.round(estimatedPayInDollars * 100),
              ...(notificationThresholdInDollars !== undefined && {
                notificationThresholdInMinorUnits: Math.round(notificationThresholdInDollars * 100),
              }),
            },
          },
        },
        requestSchema: workerPaySurveyAnswerBody,
        responseSchema: workerPaySurveyAnswerDto,
      });

      return response.data;
    },
    onSuccess: async (data, variables, context) => {
      await queryClient.invalidateQueries({
        queryKey: workerPaySurveyQueryKeys.workerPaySurveys(userId),
      });
      userOnSuccess?.(data, variables, context);
    },
    ...restOptions,
    meta: {
      logErrorMessage: APP_V2_APP_EVENTS.CREATE_WORKER_PAY_SURVEY_ANSWER_FAILURE,
      ...restOptions.meta,
    },
  });
}
